import { useState, useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "State/hooks";
import { getOrganisationInstallSummary } from "@intelligentlilli/api-layer";
import { setCacheEntry, CACHE_TTL_MS } from "State/slices/installPerformance-slice";
import { getCacheKey } from "Pages/InstallPerformance/utils";
import type { Granularity } from "Pages/InstallPerformance/utils";
import type { OrganisationInstallSummaryType } from "@intelligentlilli/api-layer";

export const useInstallPerformance = (
  orgId: number | undefined,
  granularity: Granularity,
  startTime: string,
  endTime: string
) => {
  const dispatch = useAppDispatch();
  const server = useAppSelector((state) => state.session.server);
  const cache = useAppSelector((state) => state.installPerformance.cache);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const cacheKey = orgId ? getCacheKey(orgId, granularity, startTime, endTime) : "";
  const cached = cache[cacheKey];
  const isCacheValid = Boolean(
    cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS
  );

  const fetchData = useCallback(() => {
    if (!orgId || !server) return;
    setLoading(true);
    setError(false);
    getOrganisationInstallSummary(
      server,
      orgId,
      granularity,
      startTime,
      endTime,
      "web"
    )
      .then((res) => {
        if (res.ok && res.data) {
          dispatch(
            setCacheEntry({
              key: getCacheKey(orgId, granularity, startTime, endTime),
              data: res.data,
            })
          );
        } else {
          setError(true);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [server, orgId, granularity, startTime, endTime, dispatch]);

  useEffect(() => {
    if (!isCacheValid && orgId) {
      fetchData();
    }
  }, [isCacheValid, orgId, fetchData]);

  const data: OrganisationInstallSummaryType | null = isCacheValid
    ? cached.data
    : null;

  return { data, loading, error, refetch: fetchData };
};
